import type { EffectiveFeatureCatalogueDto, FeatureKey } from '@erp/shared';

function normalizedPath(path: string): string {
  if (path.length > 1 && path.endsWith('/')) return path.slice(0, -1);
  return path;
}

function patternParts(pattern: string): readonly string[] {
  return normalizedPath(pattern).split('/').filter(Boolean);
}

export function featureRouteMatches(pattern: string, pathname: string): boolean {
  const expected = patternParts(pattern);
  const actual = patternParts(pathname);
  for (let index = 0; index < expected.length; index += 1) {
    const part = expected[index];
    if (part === '*') return true;
    if (actual[index] === undefined) return false;
    if (part?.startsWith(':')) continue;
    if (part !== actual[index]) return false;
  }
  return actual.length === expected.length;
}

function specificity(pattern: string): number {
  return patternParts(pattern).reduce(
    (score, part) => score + (part === '*' ? 0 : part.startsWith(':') ? 2 : 4),
    0,
  );
}

export function featureKeysForPath(
  catalogue: EffectiveFeatureCatalogueDto,
  pathname: string,
): readonly FeatureKey[] {
  const matches = catalogue.features.flatMap((feature) =>
    feature.frontendRoutes.flatMap((pattern) =>
      featureRouteMatches(pattern, pathname)
        ? [{ key: feature.key, specificity: specificity(pattern) }]
        : [],
    ),
  );
  const maximum = Math.max(-1, ...matches.map((match) => match.specificity));
  return [
    ...new Set(matches.filter((match) => match.specificity === maximum).map(({ key }) => key)),
  ];
}

export function isFeatureConfigurationPath(
  catalogue: EffectiveFeatureCatalogueDto,
  pathname: string,
): boolean {
  const effectiveByKey = new Map(
    catalogue.features.map((feature) => [feature.key, feature.effectiveEnabled]),
  );
  return catalogue.features.some(
    (feature) =>
      feature.dependencies.every((key) => effectiveByKey.get(key) === true) &&
      feature.configurationRoutes.some((pattern) => featureRouteMatches(pattern, pathname)),
  );
}
